import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import { useStateContext } from '../../../contexts/ContextProvider';
import type { Customer } from '../../../pages/cardex/api/useCustomer';

import Button from '../../components/Button';
import EtatErreur from '../../components/EtatErreur';
import IndicateurActualisation from '../../components/IndicateurActualisation';
import LigneConversation from '../../components/LigneConversation';
import Pills from '../../components/Pills';
import Screen from '../../components/Screen';
import { useActualisationListe } from '../../hooks/useActualisationListe';
import { useAgeDeLaDonnee } from '../../hooks/useAgeDeLaDonnee';
import { usePositionRestauree } from '../../hooks/usePositionRestauree';
import { useRechercheConversations } from '../../hooks/useRechercheConversations';
import { echecSansDonnee } from '../../helpers/requete';
import EnteteApp from '../../shell/EnteteApp';
import { useCoque } from '../../shell/CoqueContext';
import Text from '../../components/Text';
import { useConversations } from '../../api/useConversations';
import { useIdDossierFerme } from '../../api/useDossierFerme';
import { t } from '../../i18n/langue';
import type { OngletConversations } from '../../api/useConversations';
import AutresClients from './AutresClients';
import { useEtatListe } from './EtatListeContext';
import RechercheConversations from './RechercheConversations';

/**
 * La liste des conversations — #82. **La raison d'être de l'app** (cadrage n°2).
 *
 * Variante A de #16 : liste plein écran, façon messagerie grand public.
 *
 * **Trois onglets à plat**, via `Pills` du socle — pas le `Tabs` de
 * `components/`, écarté par #23, et pas un `role="tablist"` : ces pastilles ne
 * promettent aucune relation onglet ⇄ panneau.
 *
 * **Aucun filtre client.** Chaque onglet change l'URL demandée au serveur ; le
 * masquage des conversations fermées vient de `tab=inbox`, que le serveur traduit
 * en `scopeInInbox()`. La maquette filtrait en JavaScript sur la page courante —
 * sur une liste paginée à 25, « Non lus » aurait annoncé une vérité qu'il n'avait
 * pas.
 *
 * **Un seul défileur**, celui de `Screen` : #22 a écarté tout défilement imbriqué,
 * qui se dispute le balayage de retour — seul retour d'une PWA iOS. Le
 * « charger plus » vit donc au bout de la même colonne, pas dans un second cadre.
 */

// Des CLÉS, pas des libellés : une chaîne traduite au chargement du module
// resterait dans la langue du démarrage. `t()` est appelé au rendu.
const ONGLETS: { valeur: OngletConversations; cle: string }[] = [
    { valeur: 'non_lus', cle: 'nonLus' },
    { valeur: 'tous', cle: 'tous' },
    { valeur: 'a_moi', cle: 'aMoi' },
];

const VIDE: Record<OngletConversations, string> = {
    non_lus: 'nonLus',
    tous: 'tous',
    a_moi: 'aMoi',
};

interface ListeConversationsProps {
    /**
     * `closed` rend la même liste sur l'autre dossier (#85).
     *
     * Un composant paramétré plutôt qu'une copie : la ligne, la pagination et le
     * scope groupe sont exactement les mêmes, et une seconde copie aurait divergé
     * au premier correctif.
     */
    dossier?: 'inbox' | 'closed';
}

export default function ListeConversations({ dossier = 'inbox' }: ListeConversationsProps) {
    const { currentFacility } = useStateContext();
    const navigate = useNavigate();
    const { ouvrirPanneau } = useCoque();
    const queryClient = useQueryClient();

    const fermees = dossier === 'closed';
    const facilityId = currentFacility?.id ?? null;
    const estGroupe = currentFacility?.is_group === true;

    // Ce que la liste retrouve en revenant d'un fil — audit 2026-09-23 (B2).
    const { etat, enregistrer } = useEtatListe(dossier, facilityId);
    const [onglet, setOnglet] = useState<OngletConversations>(etat.onglet);
    const [page, setPage] = useState(1);
    // Les pages déjà vues, accumulées : le « charger plus » allonge la liste, il
    // ne la remplace pas. Remis à zéro dès que la demande change.
    const [pagesVues, setPagesVues] = useState<Customer[]>([]);

    // La recherche — #82. `terme` est celui, retardé de 300 ms, que le serveur
    // reçoit ; `onTermeChange` repart en page 1 comme `changerOnglet`.
    const { saisie, setSaisie, terme, effacer } = useRechercheConversations({
        initial: etat.recherche,
        onTermeChange: () => {
            setPage(1);
            setPagesVues([]);
        },
    });

    const { data, isLoading, isError, isFetching, dataUpdatedAt, refetch } = useConversations({
        facilityId,
        onglet,
        page,
        dossier,
        search: terme,
    });
    /*
     * La règle commune (audit 2026-09-23, A6) : un rechargement en échec garde la
     * liste. En page 1 l'alerte ne vient qu'à défaut de données ; en page N, les
     * lignes déjà vues restent et l'échec se dit EN BAS, là où l'on a demandé la suite.
     */
    const echec = echecSansDonnee({ isError, data });

    /*
     * L'âge de ce qu'on affiche — la contrepartie de la rétention longue (#86).
     *
     * Le cache mobile garde une heure : sans cette mention, une liste reçue tout à
     * l'heure se présenterait comme l'état actuel, et « aucun message non lu »
     * serait indiscernable de « je ne sais pas ». C'est le « zéro plausible et
     * faux » que #27 a refusé.
     *
     * Le bandeau ne suffit pas : il disparaît au retour du réseau, là où
     * l'affichage peut rester périmé encore un moment.
     */
    const age = useAgeDeLaDonnee(dataUpdatedAt);

    /*
     * Réchauffe le cache du dossier `closed` (#85). Rien n'est rendu avec : c'est
     * le FIL qui en a besoin, pour son geste de fermeture. Le charger ici — sur
     * l'écran où l'on passe du temps, mis en cache 30 min — rend la requête
     * gratuite à l'ouverture d'une conversation, qui est le moment où #24 compte.
     * Sur un lien profond venu d'une notification, le fil la paie ; c'est le
     * chemin rare.
     */
    useIdDossierFerme(facilityId);

    const conversations = useMemo(() => {
        const toutes = page === 1 ? (data?.data ?? []) : [...pagesVues, ...(data?.data ?? [])];
        // Une conversation qui reçoit un message remonte : entre deux pages elle peut
        // apparaître deux fois. Une seule ligne par client (audit 2026-09-23).
        const vus = new Set<number>();
        return toutes.filter((c) => (vus.has(c.id) ? false : (vus.add(c.id), true)));
    }, [data, page, pagesVues]);

    // Les fermées sont une archive : on y cherche une conversation, pas un client.
    const rechercheCardex = terme !== '' && !fermees;
    const idsAffiches = useMemo(() => new Set(conversations.map((c) => c.id)), [conversations]);

    const changerOnglet = (valeur: OngletConversations) => {
        setOnglet(valeur);
        setPage(1);
        setPagesVues([]);
    };

    const chargerPlus = () => {
        setPagesVues(conversations);
        setPage((p) => p + 1);
    };

    // Retrouver l'onglet et la position en revenant d'une conversation — audit
    // 2026-09-23 (B2). `Messagerie` reste monté entre `/messaging` et
    // `/messaging/:id` : cet état vit en mémoire, dans `EtatListeContext`.
    const refCorps = useRef<HTMLDivElement>(null);

    // Actualiser = repartir en page 1, sur trois chemins (lien, tirage, retour au
    // premier plan) — voir `useActualisationListe`.
    const { actualiser, actualiserALaDemande, actualisationDemandee, distance, pret } = useActualisationListe({
        queryClient,
        refCorps,
        page,
        setPage,
        setPagesVues,
        isLoading,
        isFetching,
        dataUpdatedAt,
    });

    usePositionRestauree({
        refCorps,
        onglet,
        recherche: saisie,
        defilementInitial: etat.defilement,
        enregistrer,
        isLoading,
    });

    const derniereePage = data?.meta ? data.meta.current_page >= data.meta.last_page : true;

    return (
        <Screen
            refCorps={refCorps}
            sansRebond
            entete={
                <EnteteApp
                    titre={fermees ? t('messagerie.liste.titreFermees') : t('messagerie.liste.titre')}
                    onOuvrirPanneau={ouvrirPanneau}
                />
            }
        >
            <IndicateurActualisation
                distance={distance}
                pret={pret}
                enCours={actualisationDemandee.current && isFetching && !isLoading && page === 1}
            />

            <div className="border-b border-border-color-light bg-white px-4 pb-3">
                {/*
                 * L'en-tête nomme l'établissement — #25 : « le nom de l'hôtel au
                 * plus près du geste qui coûte ». Le libellé du registre
                 * (« Messagerie ») est au-dessus, dans la coque ; il dit de quel
                 * écran il s'agit, pas de quel hôtel.
                 *
                 * En scope groupe il porte en plus une marque explicite qu'il
                 * couvre plusieurs hôtels : « Groupe Noemys » seul est vrai et
                 * inutile. C'est la ligne qui désambiguïse — encore faut-il savoir
                 * qu'il y a quelque chose à désambiguïser.
                 */}
                {/* L'en-tête d'app porte le titre ; ce bloc porte le CONTEXTE :
                    sur quel établissement on lit, et ce qu'on peut y faire. */}
                <Text variante="sousTitre" as="h2" className="truncate pt-3">
                    {currentFacility?.name ?? ''}
                </Text>
                {fermees && (
                    <Text variante="petit" ton="attenue">
                        {t('messagerie.liste.rouvrirDepuisFil')}
                    </Text>
                )}
                {!fermees && estGroupe && (
                    <Text variante="petit" ton="attenue">
                        {t('messagerie.liste.tousEtablissements')}
                    </Text>
                )}

                {/*
                 * L'âge, affiché SEULEMENT quand il commence à vouloir dire
                 * quelque chose (#86). En dessous d'une minute c'est du bruit ; au
                 * delà, c'est la différence entre « aucun message non lu » et
                 * « je ne sais pas s'il y en a ».
                 *
                 * Le lien « Actualiser », lui, est TOUJOURS là hors chargement
                 * initial : c'est le chemin accessible du geste de tirage, que
                 * VoiceOver ne pratique pas (relecture M7).
                 */}
                {!(isLoading && page === 1) && (
                    <div className="flex items-center gap-2 pt-0.5">
                        {age && (
                            <Text variante="petit" ton="attenue">
                                {t('messagerie.liste.misAJour', { age })}
                            </Text>
                        )}
                        {/* Seule une actualisation de la page 1 occupe le lien : le
                            chargement de la page suivante a son propre bouton (A6). */}
                        <Button
                            libelle={isFetching && page === 1 ? t('messagerie.liste.actualisation') : t('messagerie.liste.actualiser')}
                            variante="lien"
                            espacement="aucun"
                            disabled={isFetching && page === 1}
                            onPress={actualiserALaDemande}
                        />
                    </div>
                )}

                {/*
                 * Les trois onglets trient la BOÎTE DE RÉCEPTION (#16) — un geste
                 * permanent. Les fermées sont une archive qu'on visite exprès :
                 * « Non lus » y serait vide par construction, et « À moi » n'a plus
                 * d'objet sur ce qui est réglé.
                 */}
                {!fermees && (
                    <Pills
                        libelle={t('messagerie.liste.filtrer')}
                        options={ONGLETS.map((o) => ({ valeur: o.valeur, libelle: t(`messagerie.onglets.${o.cle}`) }))}
                        valeur={onglet}
                        onChange={changerOnglet}
                        className="mt-3"
                    />
                )}

                <RechercheConversations valeur={saisie} onChange={setSaisie} onEffacer={effacer} />
            </div>

            {/* Recherche dans la boîte : deux sections, les conversations puis le
                reste du cardex (évo Odomo 09/10/2026). */}
            {rechercheCardex && (
                <Text variante="sousTitre" as="h2" className="px-4 pb-1 pt-4">
                    {t('messagerie.liste.conversations')}
                </Text>
            )}

            {isLoading && page === 1 && (
                <Text variante="petit" ton="attenue" className="p-6 text-center">
                    {t('commun.chargement')}
                </Text>
            )}

            {echec && page === 1 && <EtatErreur onReessayer={actualiser} />}

            {!isLoading && !echec && conversations.length === 0 && (
                <Text variante="petit" ton="attenue" className="p-6 text-center">
                    {/* Distinct de la boîte vide normale (#82) : « Aucune conversation »
                        dirait qu'il n'y a rien à voir, quand il y a simplement rien qui
                        correspond au terme tapé. */}
                    {terme
                        ? t('messagerie.vide.recherche', { terme })
                        : fermees
                          ? t('messagerie.vide.fermees')
                          : t(`messagerie.vide.${VIDE[onglet]}`)}
                </Text>
            )}

            <ul>
                {conversations.map((conversation) => (
                    <li key={conversation.id} className="border-b border-border-color-light">
                        <LigneConversation
                            conversation={conversation}
                            montrerHotel={estGroupe}
                            onPress={() => navigate(`/messaging/${conversation.id}`)}
                        />
                    </li>
                ))}
            </ul>

            {echec && page > 1 && (
                <EtatErreur texte={t('messagerie.liste.erreurSuite')} onReessayer={() => void refetch()} />
            )}

            {!(echec && page > 1) && conversations.length > 0 && (!derniereePage || (isLoading && page > 1)) && (
                <div className="px-4 py-4">
                    <Button
                        libelle={isLoading || isFetching ? t('commun.chargement') : t('messagerie.liste.chargerPlus')}
                        variante="secondaire"
                        onPress={chargerPlus}
                        disabled={isLoading || isFetching}
                        pleineLargeur
                    />
                </div>
            )}

            {rechercheCardex && (
                <AutresClients
                    facilityId={facilityId}
                    terme={terme}
                    dejaAffiches={idsAffiches}
                    estGroupe={estGroupe}
                    onOuvrir={(id) => navigate(`/messaging/${id}`)}
                />
            )}
        </Screen>
    );
}
